<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import type { ScopeDescriptor } from '@/types/bbel-types'
import { SELF_SAMPLE_KEY, type SelfSampleContext } from '@/views/builder/composables/bbel-context'
import {
  ActionType,
  Combinator,
  type ConditionGroup,
  type PredicateLeaf,
  type ValidatorRule,
} from '@/types'
import {
  actionHasMessage,
  actionHasRetries,
  actionOptions,
  editableGroup,
  isAdvancedExpression,
  newPredicate,
  newRule,
  operatorNeedsValue,
  operatorOptions,
  selfFieldSuggestions,
} from './validator-options'

const props = defineProps<{
  modelValue: ValidatorRule[]
  /** BBEL scope for rule value fields — resolves `{{ wizard.* }}` / `{{ connection.* }}` references. */
  scope: ScopeDescriptor
}>()

const emit = defineEmits<{
  'update:modelValue': [value: ValidatorRule[]]
}>()

const rules = ref<ValidatorRule[]>([])

// Field suggestions descend into this service's own sampled response shape when a sample exists,
// else the fixed self.* roots (#6022).
const selfSample = inject<SelfSampleContext | undefined>(SELF_SAMPLE_KEY, undefined)
const fieldSuggestions = computed(() => selfFieldSuggestions(selfSample?.value))

const deepClone = <T,>(v: T): T => JSON.parse(JSON.stringify(v))

// Normalise each rule so its condition is an editable ALL/ANY group: a bare predicate is wrapped,
// an empty (default) expression leaf becomes a fresh group. A non-empty expression leaf is preserved
// and shown read-only (the off-UI escape hatch).
function initialize() {
  const cloned: ValidatorRule[] = deepClone(props.modelValue ?? [])
  for (const rule of cloned) {
    if (rule.condition.$type === 'predicate') {
      rule.condition = { $type: 'group', combinator: Combinator.All, rules: [rule.condition] }
    } else if (rule.condition.$type === 'expression' && rule.condition.expression.trim() === '') {
      rule.condition = { $type: 'group', combinator: Combinator.All, rules: [newPredicate()] }
    }
  }
  rules.value = cloned
}

function emitUpdate() {
  emit('update:modelValue', deepClone(rules.value))
}

const groupOf = (rule: ValidatorRule) => editableGroup(rule) as ConditionGroup
const predicatesOf = (group: ConditionGroup) =>
  group.rules.filter((c): c is PredicateLeaf => c.$type === 'predicate')

function addRule() {
  rules.value.push(newRule())
  emitUpdate()
}

function removeRule(index: number) {
  rules.value.splice(index, 1)
  emitUpdate()
}

function addCondition(rule: ValidatorRule) {
  groupOf(rule).rules.push(newPredicate())
  emitUpdate()
}

function removeCondition(group: ConditionGroup, predicate: PredicateLeaf) {
  const at = group.rules.indexOf(predicate)
  if (at >= 0) group.rules.splice(at, 1)
  if (predicatesOf(group).length === 0) group.rules.push(newPredicate())
  emitUpdate()
}

// Native drag reorder — priority is top-to-bottom, so the order of `rules` is the evaluation order.
const dragIndex = ref<number | null>(null)
const overIndex = ref<number | null>(null)

function onDragStart(index: number, e: DragEvent) {
  dragIndex.value = index
  e.dataTransfer?.setData('text/plain', String(index))
}
function onDragOver(index: number) {
  if (dragIndex.value !== null) overIndex.value = index
}
function onDrop(index: number) {
  const from = dragIndex.value
  if (from !== null && from !== index) {
    const [moved] = rules.value.splice(from, 1)
    rules.value.splice(index, 0, moved)
    emitUpdate()
  }
  dragIndex.value = null
  overIndex.value = null
}

onMounted(initialize)
</script>

<template>
  <div class="mb-3">
    <datalist id="validator-field-suggestions">
      <option v-for="f in fieldSuggestions" :key="f.path" :value="f.path">{{ f.hint }}</option>
    </datalist>

    <div
      v-for="(rule, ri) in rules"
      :key="rule.id"
      class="card mb-2"
      :class="{
        'opacity-50': dragIndex === ri,
        'border-primary': dragIndex !== null && overIndex === ri && dragIndex !== ri,
      }"
      @dragover.prevent="onDragOver(ri)"
      @drop="onDrop(ri)"
    >
      <div class="card-body p-2">
        <!-- Rule header: drag handle · name · action · remove -->
        <div class="d-flex align-items-center gap-2 mb-2">
          <span
            class="text-secondary"
            style="cursor: grab"
            title="Drag to reorder priority"
            draggable="true"
            @dragstart="onDragStart(ri, $event)"
            @dragend="dragIndex = null"
          >
            <i class="bi bi-grip-vertical"></i>
          </span>
          <input
            type="text"
            class="form-control form-control-sm flex-grow-1"
            v-model="rule.name"
            placeholder="Validator name"
            @input="emitUpdate"
          />
          <select
            class="form-select form-select-sm w-auto"
            v-model="rule.action.type"
            @change="emitUpdate"
          >
            <option v-for="a in actionOptions" :key="a.value" :value="a.value">{{ a.label }}</option>
          </select>
          <button
            type="button"
            class="btn btn-outline-danger btn-sm"
            title="Remove validator"
            @click="removeRule(ri)"
          >
            <i class="bi bi-trash"></i>
          </button>
        </div>

        <!-- Condition: a raw-expression rule is preserved and shown read-only; otherwise the group editor -->
        <div v-if="isAdvancedExpression(rule)" class="alert alert-secondary py-1 px-2 small mb-2">
          <i class="bi bi-braces me-1"></i>Advanced condition (raw expression) — edit is disabled here.
          <code class="d-block mt-1">{{ (rule.condition as any).expression }}</code>
        </div>
        <div v-else class="ms-4">
          <div class="d-flex align-items-center gap-2 mb-2 small">
            <span>Match</span>
            <select
              class="form-select form-select-sm w-auto"
              v-model="groupOf(rule).combinator"
              @change="emitUpdate"
            >
              <option :value="Combinator.All">ALL</option>
              <option :value="Combinator.Any">ANY</option>
            </select>
            <span>of:</span>
          </div>

          <div
            v-for="(pred, pi) in predicatesOf(groupOf(rule))"
            :key="pi"
            class="d-flex align-items-stretch gap-2 mb-2"
          >
            <input
              type="text"
              class="form-control form-control-sm font-monospace"
              style="max-width: 16rem"
              list="validator-field-suggestions"
              v-model="pred.field"
              placeholder="self.statusCode"
              @input="emitUpdate"
            />
            <select
              class="form-select form-select-sm w-auto"
              v-model="pred.operator"
              @change="emitUpdate"
            >
              <option v-for="o in operatorOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
            <BbelField
              v-if="operatorNeedsValue(pred.operator)"
              v-model="pred.value"
              class="flex-grow-1"
              :scope="scope"
              single-line
              placeholder="value or {{ expression }}"
              @update:model-value="emitUpdate"
            />
            <button
              type="button"
              class="btn btn-outline-danger btn-sm"
              title="Remove condition"
              @click="removeCondition(groupOf(rule), pred)"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>

          <button type="button" class="btn btn-link btn-sm text-decoration-none px-0" @click="addCondition(rule)">
            <i class="bi bi-plus"></i> Add condition
          </button>
        </div>

        <!-- Action detail: message for Fail/Warn, how often and how many times for Retry -->
        <div v-if="actionHasMessage(rule.action.type)" class="mt-1 ms-4">
          <input
            type="text"
            class="form-control form-control-sm"
            v-model="rule.action.message"
            :placeholder="rule.action.type === ActionType.Warn ? 'Warning message' : 'Failure message'"
            @input="emitUpdate"
          />
        </div>
        <div v-else-if="actionHasRetries(rule.action.type)" class="mt-1 ms-4 d-flex align-items-center gap-2">
          <label class="form-label small mb-0">Max retries</label>
          <input
            type="number"
            min="1"
            max="60"
            class="form-control form-control-sm"
            style="max-width: 5rem"
            v-model.number="rule.action.maxRetries"
            @input="emitUpdate"
          />
          <label class="form-label small mb-0 ms-2">Every</label>
          <input
            type="number"
            min="1"
            max="3600"
            placeholder="auto"
            title="Seconds between attempts. Leave empty for an automatic short backoff; set it when the answer takes time — a job that reports running until it finishes."
            class="form-control form-control-sm"
            style="max-width: 5rem"
            v-model.number="rule.action.retryIntervalSeconds"
            @input="emitUpdate"
          />
          <span class="form-label small mb-0 text-body-secondary">seconds</span>
        </div>
      </div>
    </div>

    <button type="button" class="btn btn-outline-primary btn-sm" @click="addRule">
      <i class="bi bi-plus"></i> Add validator
    </button>
  </div>
</template>
